import { i18nNsFactory } from '@atlas-frontend/i18n';
import { DirectoryTableProps, Table, TextEllipsis } from '@atlas-frontend/sdk';
import { useMemo } from 'react';
import translations from './SystemClassifierDirectoryTable.translations.json';
import { createColumnHelper } from '@tanstack/react-table';
import { SystemClassifierBasicDto } from '@atlas-frontend/sdk';
import { UiLink } from '../../paths/UiLink';
import { path } from '../../paths';

const { addTranslations, useTranslation } = i18nNsFactory(
  'system-classifier-directory-table'
);
addTranslations(translations);

const columnHelper = createColumnHelper<SystemClassifierBasicDto>();

export type SystemClassifierDirectoryTableProps = DirectoryTableProps<any>;

export function SystemClassifierDirectoryTable(
  props: SystemClassifierDirectoryTableProps
) {
  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      columnHelper.accessor('code', {
        header: t('column.code'),
        cell: (props) => (
          <TextEllipsis>
            <UiLink
              id={`system-classifier-${props.row.index}`}
              path={path.systemClassifierDetails}
              params={{ id: props.row.original.id }}
            >
              {props.renderValue()}
            </UiLink>
          </TextEllipsis>
        ),
        meta: {
          width: '40ch',
        },
      }),
      columnHelper.accessor('name', {
        header: t('column.name'),
        meta: {
          width: '40ch',
        },
      }),
      columnHelper.accessor('description', {
        header: t('column.description'),
        meta: {
          minWidth: '30ch',
        },
      }),
      columnHelper.accessor(({ itemCount }) => (itemCount ? itemCount : null), {
        id: 'itemCount',
        header: t('column.itemCount'),
        enableColumnFilter: false,
        meta: {
          width: '15ch',
          textAlign: 'right',
          className: 'itemCount',
        },
      }),
      columnHelper.accessor(
        (rowData) => t(rowData.userManaged ? 'yes' : 'no'),
        {
          id: 'userManaged',
          header: t('column.userManaged'),
          filterFn: 'includesOption',
          meta: {
            width: '15ch',
            className: 'userManaged',
          },
        }
      ),
    ],
    [t]
  );

  const options = useMemo(
    () => ({
      userManaged: [
        { id: true, name: t('yes') },
        { id: false, name: t('no') },
      ],
    }),
    [t]
  );

  return (
    <Table
      name="system-classifier-directory-table"
      columns={columns}
      options={options}
      hideClearFilters
      manualFiltering
      manualSorting
      scrollContainerSelector="#system-classifier-content"
      sticky
      virtual
      {...props}
    />
  );
}
